import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import '@adornis/chemistry/elements/components/x-loader.js';
import '@adornis/chemistry/elements/components/x-toggle-content.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { DateTimeAspect } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import '@adornis/fonts/fonts.js';
import '@adornis/popover/x-tooltip.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { examTypeBadge } from '@ils/examType/client/examTypeBadge.js';
import { promptForExamType } from '@ils/examType/client/selectExamTypePrompt.js';
import { permissionGuard } from '@ils/permissions/client/permissionGuard.js';
import '@ils/statistics/client/exd-statistics.js';
import { badgeStyleNegative, badgeStyleNeutral, badgeStylePositive } from '@ils/theme/badges.js';
import type { brandDict } from '@ils/translation/translation.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { switchMap } from 'rxjs';
import { ExamDay } from '../../db/examdays.js';
import { ExamDayFilter } from '../../db/filter/examday-filter.js';
import '../components/examday-editor/exd-filter-timebar.js';
import '../components/examday-editor/filter-pills.js';
import { ILSTestExamDialog } from '../components/ils-test-exam-dialog.js';
import { examDayListFilter } from '../store/filterStore.js';

@customElement('ils-examday-overview')
export class ILSExamdayOverview extends ChemistryLitElement {
  private readonly _filter = examDayListFilter;

  @state() private readonly _futureExamDaysCount = new RXController(this, this._filter, filter$ =>
    filter$.pipe(switchMap(filter => ExamDay.countExamdays(this._getExamDayFilter('future', filter))())),
  );
  @state() private readonly _futurePublishedExamDaysCount = new RXController(this, this._filter, filter$ =>
    filter$.pipe(
      switchMap(filter =>
        ExamDay.countExamdays(
          new ExamDayFilter({ ...this._getExamDayFilter('future', filter).toJSON(), isPublished: true }),
        )(),
      ),
    ),
  );
  @state() private readonly _pastExamDaysCount = new RXController(this, this._filter, filter$ =>
    filter$.pipe(switchMap(filter => ExamDay.countExamdays(this._getExamDayFilter('past', filter))())),
  );
  @state() private readonly _translation = new TranslationController<brandDict>(this);

  private btnTestExamDev() {
    if (baseConfig.get('DEPLOYMENT') !== 'production') {
      return html`
        <x-button
          mode="outline"
          icon="schedule"
          @click=${async () => {
            const inMinutes = await XNativeDialog.prompt(
              'In wie viel Minuten soll ein Test-Prüfungstag erstellt und geplant werden?',
            );
            if (!inMinutes || isNaN(+inMinutes)) return;

            const shortOrRegular = await XNativeDialog.showPopup<number>({
              modal: true,
              content: (resolve, reject) => {
                return html`
                  <x-flex space="md" padding="md">
                    <x-h2>Typ des Test-Examen</x-h2>
                    <x-h4>Kurze Prüfung</x-h4>

                    <x-text>
                      OSCE Dauer: 2min
                      <br />
                      5 Kandidaten
                    </x-text>

                    <x-h4>Reguläre Prüfung</x-h4>

                    <x-text
                      >OSCE Dauer: 10min (Mündlich) - 20min (Schriftlich)
                      <br />
                      9 Kandidaten
                    </x-text>

                    <x-flex horizontal space="md">
                      <x-button @click=${() => resolve(1)}>Kurz</x-button>
                      <x-button @click=${() => resolve(2)}>Regulär</x-button>
                      <x-button mode="outline" @click=${() => reject()}>abbrechen</x-button>
                    </x-flex>
                  </x-flex>
                `;
              },
            });
            if (!shortOrRegular) return;

            const jobID = new Date().toString();
            const examDayIDPromise = ExamDay.scheduleTestExamDayInMinutes(+inMinutes, shortOrRegular, jobID)();

            setTimeout(() => {
              ILSTestExamDialog.showPopup({
                closeOnNavigate: false,
                props: {
                  jobID,
                },
              });
            }, 100);

            const examDayID = await examDayIDPromise;

            if (!examDayID) return;
            goTo({
              path: '/start/scheduling',
              searchParams: new URLSearchParams({ day: examDayID }),
            });
          }}
        >
          Test-Examen erstellen
        </x-button>
      `;
    } else {
      return nothing;
    }
  }

  override render() {
    return html`
      <x-flex padding="md" ${css({ overflow: 'auto', width: '100%' })}>
        <x-flex space="xl" ${css({ maxWidth: this.sizes.maxPageWidth, height: '100%' })}>
          <x-flex space="md">
            <exd-filter-pills></exd-filter-pills>
            <exd-filter-timebar></exd-filter-timebar>
            ${permissionGuard({
              resource: 'examdays',
              action: 'viewstatistics',
              guardedTemplate: html` <exd-statistics></exd-statistics> `,
            })}
            <x-toggle-content
              open
              ${css({ width: '100%' })}
              .label=${html`
                <x-flex horizontal space="sm" crossaxis-center space-between w100>
                  <x-h2>
                    Anstehende Prüfungstage (veröffentlicht: ${this._futurePublishedExamDaysCount.value} /
                    ${this._futureExamDaysCount.value})
                  </x-h2>
                  ${permissionGuard({
                    resource: 'examdays',
                    action: 'create',
                    guardedTemplate: html`
                      <div>
                        ${this.btnTestExamDev()}
                        <x-button
                          icon="add"
                          @click=${async () => {
                            const examType = await promptForExamType(
                              'Für welchen ExamType soll ein neues ExamDay erstellt werden?',
                            );
                            const examDayID = await ExamDay.createNewExamDay(examType)();
                            goTo({ path: `/start/editor-exam/${examDayID}`, searchParams: NO_CHANGE });
                          }}
                        >
                          Neuen Prüfungstag anlegen
                        </x-button>
                      </div>
                    `,
                  })}
                </x-flex>
              `}
              .content=${() =>
                html`
                  <x-data-view
                    .options=${{
                      class: ExamDay,
                      operation: 'subscribeExamDayByFilter',
                      loader: () =>
                        html` <x-flex ${css({ minHeight: '200px' })} center crossaxis-center>
                          <ils-loader></ils-loader>
                        </x-flex>`,
                      customColumns: this._getColumnDefinitions('future'),
                      view: new View<typeof ExamDay>({
                        filter: this._getExamDayFilter('future', this._filter.value),
                      }),
                    } satisfies DataViewOptions<typeof ExamDay>}
                  ></x-data-view>
                `}
            >
            </x-toggle-content>
            <x-hr></x-hr>
            <x-toggle-content
              ${css({ width: '100%' })}
              .label=${html` <x-h2> Vergangene Prüfungen (${this._pastExamDaysCount.value}) </x-h2> `}
              .content=${() => html`
                <x-data-view
                  .options=${{
                    class: ExamDay,
                    operation: 'subscribeExamDayByFilter',
                    loader: () =>
                      html` <x-flex ${css({ minHeight: '200px' })} center crossaxis-center>
                        <ils-loader></ils-loader>
                      </x-flex>`,
                    customColumns: this._getColumnDefinitions('past'),
                    view: new View<typeof ExamDay>({
                      filter: this._getExamDayFilter('past', this._filter.value),
                    }),
                    isSortable: false,
                  } satisfies DataViewOptions<typeof ExamDay>}
                ></x-data-view>
              `}
            >
            </x-toggle-content>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  private async copyExamDay(examDay: ExamDay) {
    void XNativeDialog.guard(() => ExamDay.copyExamDay(examDay._id)(), {
      confirmText: `Prüfungstag ${examDay.name} kopieren?`,
      hasToConfirm: true,
      showLoaderModal: true,
      success: async result => {
        const open = await XNativeDialog.confirm(
          `Prüfungstag ${examDay.name} erfolgreich kopiert! Zum editieren öffnen?`,
        );
        if (!open) return;
        goTo({ path: `/start/editor-exam/${result}`, searchParams: NO_CHANGE });
      },
    });
  }

  private _getColumnDefinitions(type: 'past' | 'future'): Array<ColumnDefinition<typeof ExamDay>> {
    return [
      {
        customID: 'name+location',
        columnName: '',
        columnWidth: '1fr',
        renderItem: examDay => {
          return html`
            <x-flex space="sm">
              <x-text> ${examDay.name} </x-text>
              <x-flex horizontal crossaxis-center space="xxs">
                ${examDay.location
                  ? html`
                      <x-text tone="subtle"> ${examDay.location.toString()} </x-text>
                      <x-icon ${css({ opacity: '0' })}> warning </x-icon>
                    `
                  : html`
                      <x-icon ${css({ color: this.colors.tone.warning })}> warning </x-icon>
                      <x-text tone="warning"> Ohne Standort </x-text>
                    `}
              </x-flex>
            </x-flex>
          `;
        },
        selectionSet: {
          name: 1,
          location: {
            displayName: 1,
            address: {
              name: 1,
              streetAndHousenumber: 1,
              zipCity: { zip: 1, city: 1 },
              bundesland: 1,
              country: 1,
              additionalInfo: 1,
            },
          },
        },
      },
      {
        fieldName: 'examType',
        columnName: '',
        columnWidth: 'min-content',
        renderItem: examDay => examTypeBadge(examDay.examType, examDay.ratingType),
        selectionSet: {
          examType: 1,
          ratingType: 1,
        },
      },
      ...(type === 'past'
        ? ([
            {
              fieldName: 'sentResults',
              columnName: '',
              columnWidth: 'max-content',
              selectionSet: {
                candidateIDs: 1,
                candidates: {
                  profile: {
                    resultsEmailState: {
                      order: {
                        products: {
                          product: {
                            _id: 1,
                            reference: 1,
                          },
                        },
                      },
                    },
                  },
                },
              } as const,
              renderItem: instance => {
                const sentResults = instance.allResultsSent();
                const display = sentResults === 'all' || sentResults === 'some';
                return html`
                  <x-flex horizontal crossaxis-center space="xs">
                    <x-icon
                      ${css({
                        ...(display ? {} : { opacity: '0', pointerEvents: 'none' }),
                        ...(sentResults === 'all' ? badgeStylePositive() : badgeStyleNeutral()),
                      })}
                    >
                      school
                    </x-icon>
                    ${display
                      ? html`
                          <x-tooltip
                            .text=${html`
                              <x-text>
                                ${sentResults === 'all'
                                  ? 'Ergebnisse versandt'
                                  : 'Nicht alle Ergebnisse wurden versandt'}
                              </x-text>
                            `}
                          ></x-tooltip>
                        `
                      : nothing}
                  </x-flex>
                `;
              },
            },
            {
              fieldName: 'sentCertificates',
              columnName: '',
              columnWidth: 'max-content',
              selectionSet: {
                candidateIDs: 1,
                numPassedUsers: 1,
                numRatedUsers: 1,
                candidates: {
                  profile: {
                    certificateEmailState: {
                      order: {
                        products: {
                          product: {
                            reference: 1,
                          },
                        },
                      },
                    },
                  },
                },
              } as const,
              renderItem: instance => {
                const sentCerts = instance.allCertificatesSent();
                const display = sentCerts === 'all' || sentCerts === 'some';
                return html`
                  <x-flex horizontal crossaxis-center space="xs">
                    <div>
                      <x-icon
                        ${css({
                          ...(display ? {} : { opacity: '0', pointerEvents: 'none' }),
                          ...(sentCerts === 'all' ? badgeStylePositive() : badgeStyleNeutral()),
                        })}
                      >
                        fact_check
                      </x-icon>
                    </div>
                    ${display
                      ? html`
                          <x-tooltip
                            .text=${html`
                              <x-flex space="sm">
                                <x-text>
                                  ${sentCerts === 'all' ? 'Urkunden versandt' : 'Nicht alle Urkunden wurden versandt'}
                                </x-text>
                              </x-flex>
                            `}
                          ></x-tooltip>
                        `
                      : nothing}
                  </x-flex>
                `;
              },
            },
            {
              fieldName: 'numRatedUsers',
              columnName: '',
              columnWidth: 'max-content',
              selectionSet: { numRatedUsers: 1, numParticipatedUsers: 1 } as const,
              renderItem: instance => html`
                <x-button
                  mode="text"
                  @click=${() => {
                    goTo({
                      path: '/start/evaluation-overview',
                      searchParams: new URLSearchParams({ day: instance._id }),
                    });
                  }}
                >
                  <x-flex horizontal crossaxis-center space="xs">
                    <x-icon
                      ${css({
                        color:
                          (instance.numRatedUsers ?? -2) >= (instance.numParticipatedUsers ?? -1) ? 'green' : 'inherit',
                      })}
                    >
                      ${(instance.numRatedUsers ?? -2) >= (instance.numParticipatedUsers ?? -1) ? 'check' : 'grading'}
                    </x-icon>
                    <x-text ${css({ width: '5ch' })}>
                      ${instance.numRatedUsers}/${instance.numParticipatedUsers}
                    </x-text>
                    <x-tooltip .text=${html` <x-text> Fertige Bewertungen </x-text> `}></x-tooltip>
                  </x-flex>
                </x-button>
              `,
            },
          ] as ColumnDefinition<typeof ExamDay>[])
        : // if future
          ([
            {
              fieldName: 'published',
              columnName: '',
              columnWidth: 'max-content',
              renderItem: instance => html`
                <div>
                  ${instance.published
                    ? html`<x-text ${css(badgeStylePositive())}> Veröffentlicht </x-text>`
                    : html`<div></div>`}
                </div>
              `,
            },
            {
              fieldName: 'examCenter',
              columnName: '',
              columnWidth: 'max-content',
              renderItem: instance => html`
                <div>
                  ${!instance.examCenter?.length // TODO how do we handle locations here? Because this is implicitly assigned by setting a location on the examday
                    ? html`<x-text ${css(badgeStyleNegative())}> Aufsicht fehlt </x-text>`
                    : html`<div></div>`}
                </div>
              `,
            },
            {
              fieldName: 'osceTesterTuples',
              columnName: '',
              columnWidth: '90px',
              selectionSet: {
                start: 1,
                end: 1,
                osceTesterTuples: {
                  testerIDs: 1,
                  osce: {
                    _id: 1,
                    taskType: 1,
                    durationInMinutes: 1,
                  },
                },
                slotPause: 1,
                blockPause: 1,
                breakInMinutes: 1,
                breakAfterRounds: 1,
              } as const,
              renderItem: instance => {
                const maxTesters = instance.getMaxTesters();
                const testers = instance.getAllocatedTesters();

                return html`
                  <x-flex horizontal crossaxis-center space="xs">
                    <x-icon ${css({ fontSize: '22px' })}>theater_comedy</x-icon>
                    <x-text ${css({ minWidth: '30px' })}> ${testers}/${maxTesters} </x-text>
                    <x-tooltip .text=${html` <x-text> Schauspieler </x-text> `}></x-tooltip>
                  </x-flex>
                `;
              },
            },
          ] as ColumnDefinition<typeof ExamDay>[])),
      {
        fieldName: 'maxCandidates',
        columnWidth: '95px',
        columnName: '',
        selectionSet: {
          candidateIDs: 1,
          maxCandidates: 1,
          participatedOSCEs: 1,
          osceTesterTuples: {
            osce: {
              _id: 1,
              taskType: 1,
              durationInMinutes: 1,
            },
          },
        },
        renderItem: instance => {
          const osceIDs = instance.osceTesterTuples.map(t => t.osce?._id);
          const warning =
            type === 'future'
              ? osceIDs.some(osce => (osce ? instance.participatedOSCEs?.includes(osce) : false))
              : false;

          if (type === 'past') {
            return html`
              <x-flex horizontal crossaxis-center space="xs">
                <x-icon ${css({ fontSize: '22px' })}>group</x-icon>
                <x-flex horizontal crossaxis-center>
                  <x-text ${css({ minWidth: '30px' })}>
                    ${instance.candidateIDs.length}/${instance.maxCandidates}
                  </x-text>
                </x-flex>
                <x-tooltip .text=${html` <x-text> Prüflinge </x-text> `}></x-tooltip>
              </x-flex>
            `;
          } else {
            return html`
              <x-flex
                horizontal
                crossaxis-center
                space="xs"
                ${css({ color: warning ? this.colors.tone.warning : undefined })}
              >
                <x-icon ${css({ fontSize: '22px' })}>group</x-icon>
                <x-flex horizontal crossaxis-center>
                  <x-text ${css({ minWidth: '30px' })}>
                    ${instance.candidateIDs.length}/${instance.maxCandidates}
                  </x-text>
                  ${warning ? html` <x-icon> warning </x-icon> ` : nothing}
                </x-flex>
                ${warning
                  ? html`
                      <x-tooltip
                        .text=${html`
                          <x-flex horizontal crossaxis-center space="sm">
                            <x-icon ${css({ color: this.colors.tone.warning })}> warning </x-icon>
                            <x-text> Prüfling hat mind. an einem der OSCE's bereits teilgenommen </x-text>
                          </x-flex>
                        `}
                      ></x-tooltip>
                    `
                  : html` <x-tooltip .text=${html` <x-text> Prüflinge </x-text> `}></x-tooltip> `}
              </x-flex>
            `;
          }
        },
      },
      {
        customID: '_copyEdit',
        columnWidth: 'min-content',
        renderItem: instance => html`
          <x-flex horizontal space="sm">
            ${permissionGuard({
              resource: 'examdays',
              action: 'create',
              guardedTemplate: html`
                <x-button mode="text" icon="content_copy" @click=${() => this.copyExamDay(instance)}>
                  <x-tooltip .text=${html` <x-text> Prüfungstag kopieren </x-text> `}></x-tooltip>
                </x-button>
              `,
            })}
            ${permissionGuard({
              resource: 'examdays',
              action: 'modify',
              guardedTemplate: html`
                <x-button
                  mode="text"
                  icon="edit"
                  @click=${() =>
                    goTo({
                      path: `/start/editor-exam/${instance._id}`,
                      searchParams: NO_CHANGE,
                    })}
                >
                  <x-tooltip .text=${html` <x-text> Prüfungstag bearbeiten </x-text> `}></x-tooltip>
                </x-button>
              `,
            })}
          </x-flex>
        `,
      },
    ];
  }

  private _getExamDayFilter(type: 'past' | 'future', filter: ExamDayFilter) {
    const originalFilter = filter.toJSON();
    return new ExamDayFilter({
      ...originalFilter,
      sortingFields: ['start'],
      sortingDirections: type === 'past' ? ['desc'] : ['asc'],
      aspects:
        type === 'past'
          ? [
              new DateTimeAspect({ secondValue: new Date(), mode: 'before', fieldName: 'start' }),
              ...originalFilter.aspects,
            ]
          : [
              new DateTimeAspect({ firstValue: new Date(), mode: 'after', fieldName: 'start' }),
              ...originalFilter.aspects,
            ],
    });
  }
}
